import { Card } from "primereact/card";
import * as client from "../../../client";
import * as OL from "../OverrideableList";
import { Panel } from "primereact/panel";
import {
  applyOverridesToAddress,
  applyOverridesToCompanyInfo,
  applyOverridesToResponse,
} from "../Conversion";
import styles from "./GeneralInput.module.css";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { cloneDeep, isEqual } from "lodash";
import { useUpdateReport } from "../data";
import { flaggedHighlightColor } from "../dashboard/util";
import { ThemeContext } from "../../../themes/Theme";
import { Tooltip } from "primereact/tooltip";
import { v4 as uuid } from "uuid";
import { EnumEditor } from "../sov/CellEditors";
import { InputTextarea } from "primereact/inputtextarea";
import { GoogleSheetsModal } from "../sov/GoogleSheetsModal";
import { BUSINESS_DESCRIPTIONS } from "./business-descriptions";
import { Toast } from "primereact/toast";
import { isValidEffectiveDate } from "./util";
import { workflowInformation } from "../attachments/util";
import { useClientId } from "../../../ClientConfig";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import { ClientId, perClient } from "../../../client-features";

const REQUIRED_FIELDS = perClient<(keyof client.RawCompanyInfo)[]>({
  bluesky: [
    // Specifically requested
    "effective_date",
    "expiration_date",
    "named_insured",
    "business_description",
    "entity_type",
    "years_in_business",
    "company_address",
    "company_city",
    "company_state",
    "company_zip",
  ],
});

const REQUIRED_FOR_BSP_WRITE = perClient<(keyof client.RawCompanyInfo)[]>({
  bluesky: [
    "named_insured",
    "effective_date",
    "broker_agent_name",
    "broker_email",
    "broker_name",
  ],
});

type FlagIds = client.CompanyInfo["private_flags"][string][number];

const flagTextForCompanyInfoFlag = (companyInfoFlag: FlagIds) => {
  if (typeof companyInfoFlag === "string") {
    switch (companyInfoFlag) {
      case "low_confidence":
        return "We have low confidence in this value";
      case "medium_confidence":
        return "We have medium confidence in this value";
    }
  }

  switch (companyInfoFlag.flag_id) {
    case "year_incorporated_derived_from_search":
      return (
        "The year incorporated was found via a web search. Our primary source was " +
        companyInfoFlag.source
      );
  }
};

const flagTextForCompanyInfoField = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo
) => {
  const privateFlags =
    field in companyInfo.private_flags ? companyInfo.private_flags[field] : [];
  const allFlagTexts = [];
  for (const flag of privateFlags) {
    allFlagTexts.push(flagTextForCompanyInfoFlag(flag));
  }

  if (allFlagTexts.length === 0) {
    return "";
  }

  return allFlagTexts.map((text) => `- ${text}`).join("\n");
};

const severityForFlag = (companyInfoFlag: FlagIds): number => {
  if (typeof companyInfoFlag === "string") {
    switch (companyInfoFlag) {
      case "low_confidence":
        return 81;
      case "medium_confidence":
        return 65;
    }
  }

  switch (companyInfoFlag.flag_id) {
    case "year_incorporated_derived_from_search":
      return 65;
  }
};

const maxSeverityForCompanyInfoField = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo
): number => {
  const severities = (companyInfo.private_flags[field] ?? [])
    .map((flag) => severityForFlag(flag))
    .concat(0);
  return Math.max(...severities);
};

const elementForFlags = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo
) => {
  const flags = companyInfo.private_flags[field] ?? [];
  const yearIncorporationSearchFlag = flags.find(
    (flag) =>
      !(typeof flag === "string") &&
      flag.flag_id === "year_incorporated_derived_from_search"
  );
  if (yearIncorporationSearchFlag) {
    return (
      <Button
        icon="pi pi-external-link"
        size="small"
        style={{ height: "24px", width: "24px", padding: "0px" }}
        text
        severity="secondary"
        onClick={() => {
          if (
            !(typeof yearIncorporationSearchFlag === "string") &&
            yearIncorporationSearchFlag.flag_id ===
              "year_incorporated_derived_from_search"
          ) {
            window.open(yearIncorporationSearchFlag.source, "_blank");
          }
        }}
      />
    );
  }
  return <></>;
};

const requiredTextForField = (
  clientId: ClientId,
  field: keyof client.RawCompanyInfo
) => {
  if (
    REQUIRED_FIELDS[clientId]?.includes(field) &&
    REQUIRED_FOR_BSP_WRITE[clientId]?.includes(field)
  ) {
    return "This field is required for client file folder creation and rating";
  }
  if (REQUIRED_FIELDS[clientId]?.includes(field)) {
    return "This field is required for rating";
  }
  if (REQUIRED_FOR_BSP_WRITE[clientId]?.includes(field)) {
    return "This field is required for client file folder creation";
  }
};

const flagStyles = ({
  hasOverride,
  maxSeverity,
  isLightMode,
  isRequiredAndIsBlank,
  isBlank,
}: {
  hasOverride: boolean;
  maxSeverity: number;
  isLightMode: boolean;
  isBlank: boolean;
  isRequiredAndIsBlank: boolean;
}) => {
  const inputTextStyles: React.CSSProperties = {
    height: "24px",
    width: "200px",
    boxShadow:
      hasOverride || maxSeverity || isRequiredAndIsBlank
        ? `0 0 0 2px ${flaggedHighlightColor(
            maxSeverity,
            isLightMode,
            hasOverride,
            isRequiredAndIsBlank
          )}`
        : "",
  };
  const labelStyles: React.CSSProperties =
    hasOverride || maxSeverity || isRequiredAndIsBlank
      ? {
          backgroundColor: flaggedHighlightColor(
            maxSeverity,
            isLightMode,
            hasOverride,
            isRequiredAndIsBlank
          ),
          padding: "2px",
          height: isBlank ? "23px" : "auto",
          minWidth: isBlank ? "44px" : "auto",
        }
      : {};
  return { labelStyles, inputTextStyles };
};

type AdditionalInterestFlagIds =
  client.OverrideableListElementRawAdditionalInterestLiteralTodo["flags"][string];

type InterestList = OL.OverrideableListElement<
  client.RawAdditionalInterest,
  AdditionalInterestFlagIds
>[];

const formattedAddress = (address: client.RawAddress): string => {
  const street = address.geocoded_street_address;
  const city = address.city;
  const state = address.state;
  const location = address.premises_number ?? "None";
  const building = address.building_number ?? "None";
  return `${street}, ${city} ${state} (Loc ${location}, Bldg ${building})`;
};

const EditableAdditionalInterests = ({
  additionalInterests,
  setAdditionalInterests,
  isEditing,
  allAddresses,
}: {
  additionalInterests: InterestList;
  setAdditionalInterests: Dispatch<SetStateAction<InterestList>>;
  isEditing: boolean;
  allAddresses: client.Address[];
}) => {
  return (
    <>
      {additionalInterests
        .map((element) => {
          const values = OL.effectiveValues(element);
          return values ? ([values, element] as const) : undefined;
        })
        .filter((v) => v !== undefined)
        .map(([values, element], index) => {
          const onEdit = (
            field: keyof client.RawAdditionalInterest,
            value: string
          ) => {
            setAdditionalInterests((interests) => {
              return interests.map((a, i) =>
                i === index ? OL.overriden(a, { [field]: value }) : a
              );
            });
          };

          const onEditAddress = (value: string) => {
            setAdditionalInterests((interests) => {
              return interests.map((a, i) =>
                i === index ? OL.overriden(a, { linked_address_id: value }) : a
              );
            });
          };

          return (
            <Panel style={{ alignSelf: "start" }}>
              <div className={styles.additionalInterestWrapper}>
                <EditableAIFlaggedField
                  label="Entity Name"
                  field="entity_name"
                  isEditing={isEditing}
                  listElement={element}
                  values={values}
                  onEdit={onEdit}
                />
                <EditableAIFlaggedField
                  label="Entity Address"
                  field="entity_address"
                  isEditing={isEditing}
                  listElement={element}
                  values={values}
                  onEdit={onEdit}
                />
                <EditableAIFlaggedField
                  label="Interest type"
                  field="form_name"
                  isEditing={isEditing}
                  listElement={element}
                  values={values}
                  onEdit={onEdit}
                  enumValues={[
                    "LENDER'S LOSS PAYABLE",
                    "LOSS PAYEE",
                    "MORTAGEE",
                  ]}
                />
                <EditableAddressField
                  label="Building Address"
                  isEditing={isEditing}
                  addresses={allAddresses}
                  onEdit={onEditAddress}
                  listElement={element}
                />
                {isEditing && (
                  <Button
                    label="Remove"
                    size="small"
                    severity="danger"
                    text
                    onClick={() => {
                      setAdditionalInterests((interests) =>
                        interests.map((val, i) =>
                          i === index ? OL.deleted(val) : val
                        )
                      );
                    }}
                  />
                )}
              </div>
            </Panel>
          );
        })}
      {isEditing && (
        <Button
          size="small"
          style={{ alignSelf: "start" }}
          text
          raised
          label="Add"
          onClick={() =>
            setAdditionalInterests((interests) =>
              interests.concat(
                OL.inserted<
                  client.RawAdditionalInterest,
                  AdditionalInterestFlagIds
                >({
                  entity_name: "",
                  entity_address: "",
                  form_name: "",
                  linked_address_id: null,
                })
              )
            )
          }
        />
      )}
    </>
  );
};

type EditableFlaggedFieldProps = {
  rawCompanyInfo: client.CompanyInfo;
  safeCompanyInfo: client.RawCompanyInfo;
  setSafeCompanyInfo: Dispatch<SetStateAction<client.RawCompanyInfo>>;
  field: keyof client.RawCompanyInfo;
  label: string;
  isEditing: boolean;
  enumValues?: string[];
  placeholder?: string;
  disableEditingTooltip?: string;
};

const EditableAddressField = ({
  listElement,
  addresses,
  label,
  isEditing,
  onEdit,
}: {
  listElement: OL.OverrideableListElement<
    client.RawAdditionalInterest,
    AdditionalInterestFlagIds
  >;
  addresses: client.Address[];
  label: string;
  isEditing: boolean;
  onEdit: (newVal: string) => void;
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const linkedAddressId = OL.effectiveValues(listElement)?.linked_address_id;
  const field = "linked_address_id";
  const formattedAddressToAddress = useMemo(
    () =>
      Object.fromEntries(
        addresses.map((a) => [formattedAddress(applyOverridesToAddress(a)), a])
      ),
    [addresses]
  );

  const hasOverride =
    listElement.overrides[field] !== undefined &&
    listElement.original !== null &&
    listElement.overrides[field] !== listElement.original[field];

  const tooltipId = `tooltip-${uuid()}`;
  const matchingAddress = addresses.find(
    (a) => a.address_id === linkedAddressId
  );
  const hasAddress = !!matchingAddress;
  const extraFlag: [string, number] | undefined = !hasAddress
    ? ["We couldn't find a building/location number for this interest.", 70]
    : undefined;
  const maxSeverity = extraFlag?.[1] ?? 0;

  const flagText =
    extraFlag?.[0] ?? (hasOverride ? "A user edited this value." : "");

  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank: !matchingAddress,
    isRequiredAndIsBlank: false,
  });

  return (
    <div className={styles.horizontalContainer}>
      <span style={{ fontWeight: "bold", width: "150px", fontSize: "14px" }}>
        {label}
      </span>
      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        <EnumEditor
          options={{
            value: matchingAddress
              ? formattedAddress(applyOverridesToAddress(matchingAddress))
              : null,
            editorCallback: (val) =>
              onEdit(formattedAddressToAddress[val]?.address_id),
          }}
          enumValues={Object.keys(formattedAddressToAddress).concat("None")}
          additionalStyles={{
            ...inputTextStyles,
            height: "26px",
            minWidth: "500px",
          }}
          disabled={false}
          pt={{ input: { style: { paddingTop: "4px" } } }}
        />
      ) : (
        <div style={{ height: "26px", display: "flex", alignItems: "center" }}>
          <span
            style={labelStyles}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          >
            {matchingAddress
              ? formattedAddress(applyOverridesToAddress(matchingAddress))
              : ""}
          </span>
        </div>
      )}
    </div>
  );
};

const EditableAIFlaggedField = <
  T extends { [field: string]: string | null },
  F
>({
  listElement,
  values,
  field,
  label,
  isEditing,
  onEdit,
  enumValues,
}: {
  listElement: OL.OverrideableListElement<T, F>;
  values: T;
  field: keyof T & string;
  label: string;
  isEditing: boolean;
  onEdit: (field: keyof T & string, newVal: string) => void;
  enumValues?: string[];
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const hasOverride =
    listElement.overrides[field] !== undefined &&
    listElement.original !== null &&
    listElement.overrides[field] !== listElement.original[field];

  const maxSeverity = 0;

  const flagText = hasOverride
    ? `A user edited this value. The original value was "${
        listElement.original?.[field] ?? "None"
      }".`
    : "";
  const tooltipId = `tooltip-${uuid()}`;
  const value = values[field];

  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank: !value,
    isRequiredAndIsBlank: false,
  });

  return (
    <div className={styles.horizontalContainer}>
      <span style={{ fontWeight: "bold", width: "150px", fontSize: "14px" }}>
        {label}
      </span>
      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        enumValues ? (
          <EnumEditor
            options={{ value, editorCallback: (val) => onEdit(field, val) }}
            enumValues={enumValues}
            additionalStyles={{
              ...inputTextStyles,
              height: "26px",
              minWidth: "500px",
            }}
            disabled={false}
            pt={{ input: { style: { paddingTop: "4px" } } }}
          />
        ) : (
          <InputText
            style={inputTextStyles}
            width="500px"
            value={value ?? undefined}
            onChange={(e) => onEdit(field, e.target.value)}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          />
        )
      ) : (
        <div style={{ height: "26px", display: "flex", alignItems: "center" }}>
          <span
            style={labelStyles}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          >
            {value}
          </span>
        </div>
      )}
    </div>
  );
};

const EditableFlaggedField: React.FC<EditableFlaggedFieldProps> = ({
  rawCompanyInfo,
  safeCompanyInfo,
  field,
  label,
  isEditing,
  setSafeCompanyInfo,
  enumValues,
  placeholder,
  disableEditingTooltip,
}) => {
  const clientId = useClientId();
  const currentValue =
    field === "additional_named_insureds"
      ? (safeCompanyInfo.additional_named_insureds ?? []).join("\n")
      : safeCompanyInfo[field]?.toString() ?? "";
  const [textValue, setTextValue] = useState(currentValue);
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const hasOverride = field in rawCompanyInfo.private_overrides;
  const isBlank = currentValue === "";
  const isRequiredAndIsBlank =
    isBlank &&
    !!(
      REQUIRED_FIELDS[clientId]?.includes(field) ||
      REQUIRED_FOR_BSP_WRITE[clientId]?.includes(field)
    );

  const maxSeverity = maxSeverityForCompanyInfoField(rawCompanyInfo, field);

  let flagText = isRequiredAndIsBlank
    ? requiredTextForField(clientId, field)
    : hasOverride
    ? `A user edited this value. The original value was "${
        rawCompanyInfo.private_original_values[field] || "None"
      }".`
    : flagTextForCompanyInfoField(rawCompanyInfo, field);

  if (disableEditingTooltip) {
    if (flagText !== "") {
      flagText += "\n\n";
    }
    flagText += disableEditingTooltip;
  }
  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank,
    isRequiredAndIsBlank,
  });

  const tooltipId = `tooltip-${uuid()}`;

  const onEdit = (newValue: string) => {
    setSafeCompanyInfo((oldCompanyInfo) => {
      const newCompanyInfo = { ...oldCompanyInfo };
      if (field === "additional_named_insureds") {
        const newValues = newValue
          .split("\n")
          .map((value) => value.trim())
          .filter((value) => value !== "");

        newCompanyInfo[field] = newValues;
      } else if (field === "primary_premium" || field === "excess_premium") {
        // TODO: check for nan if we ever allow editing to these
        newCompanyInfo[field] = parseFloat(newValue);
      } else {
        newCompanyInfo[field] = newValue;
      }
      return newCompanyInfo;
    });
  };

  useEffect(() => {
    if (field === "additional_named_insureds") {
      const newValues = textValue
        .split("\n")
        .map((value) => value.trim())
        .filter((value) => value !== "");
      if (!isEqual(newValues, safeCompanyInfo[field])) {
        onEdit(newValues.join("\n"));
      }
    }
  }, [textValue]);

  return (
    <div className={styles.horizontalContainer}>
      {field !== "additional_named_insureds" && (
        <span style={{ fontWeight: "bold", width: "150px", fontSize: "14px" }}>
          {label}
        </span>
      )}

      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        enumValues ? (
          <EnumEditor
            options={{ value: currentValue, editorCallback: onEdit }}
            enumValues={enumValues}
            additionalStyles={{ ...inputTextStyles, height: "26px" }}
            disabled={false}
            pt={{ input: { style: { paddingTop: "4px" } } }}
          />
        ) : field === "additional_named_insureds" ? (
          <InputTextarea
            style={{ ...inputTextStyles, height: "auto", width: "360px" }}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
            rows={4}
            placeholder="Separate with newlines"
          />
        ) : (
          <div
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
            style={{ display: "inline-block" }}
          >
            <InputText
              style={inputTextStyles}
              value={currentValue}
              onChange={(e) => onEdit(e.target.value)}
              placeholder={placeholder}
              disabled={disableEditingTooltip !== undefined}
            />
          </div>
        )
      ) : (
        <>
          {field === "additional_named_insureds" ? (
            <div className={styles.verticalContainer}>
              {safeCompanyInfo.additional_named_insureds?.map((name) => (
                <span
                  key={name}
                  style={labelStyles}
                  data-pr-tooltip={flagText}
                  data-pr-showdelay={500}
                  id={tooltipId}
                >
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <div
              style={{ height: "26px", display: "flex", alignItems: "center" }}
            >
              <span
                style={{
                  ...labelStyles,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                data-pr-tooltip={flagText}
                data-pr-showdelay={500}
                id={tooltipId}
              >
                {currentValue}
                {!hasOverride && elementForFlags(rawCompanyInfo, field)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

type GenearlInputProps = {
  specialtyPropertyInfo: client.ReportResponse;
};

export const GeneralInput: React.FC<GenearlInputProps> = ({
  specialtyPropertyInfo,
}) => {
  const clientId = useClientId();
  const addresses = useMemo(
    () =>
      specialtyPropertyInfo.report_json.sovs
        .filter((sov) => sov.is_enabled)
        .flatMap((sov) => sov.addresses),
    [specialtyPropertyInfo.report_json]
  );
  const rawCompanyInfo = specialtyPropertyInfo.report_json.company_info;
  const originalSafeCompanyInfo = applyOverridesToCompanyInfo(rawCompanyInfo);
  const [isEditing, setIsEditing] = useState(false);
  const [safeCompanyInfo, setSafeCompanyInfo] = useState(
    originalSafeCompanyInfo
  );
  const [additionalInterests, setAdditionalInterests] = useState(
    rawCompanyInfo.additional_interests ?? []
  );
  const [googleSheetsModalVisible, setGoogleSheetsModalVisible] =
    useState(false);
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);
  const toast = useRef<Toast>(null);
  const workflowState = workflowInformation(
    clientId,
    specialtyPropertyInfo.workflow_metadata,
    applyOverridesToCompanyInfo(specialtyPropertyInfo.report_json.company_info)
  );

  return (
    <>
      <Toast ref={toast} />
      <Card
        header={
          <div
            className={styles.horizontalContainer}
            style={{
              paddingTop: "20px",
              paddingLeft: "20px",
            }}
          >
            <span className="p-card-title" style={{ marginRight: "12px" }}>
              General
            </span>
            {!isEditing && (
              <>
                <IfFeatureEnabled feature="bluesky-populate-google-sheets">
                  <Button
                    disabled={updateReport.isPending}
                    label="Populate Google Sheets"
                    onClick={() => setGoogleSheetsModalVisible(true)}
                    icon="pi pi-google"
                    raised
                    text
                  />
                </IfFeatureEnabled>
                <Button
                  disabled={updateReport.isPending}
                  label="Edit"
                  onClick={() => setIsEditing(true)}
                  icon="pi pi-pencil"
                  raised
                  severity="secondary"
                  text
                />
              </>
            )}
            {isEditing && (
              <>
                <Button
                  label="Cancel"
                  disabled={updateReport.isPending}
                  onClick={() => {
                    setSafeCompanyInfo(originalSafeCompanyInfo);
                    setAdditionalInterests(
                      rawCompanyInfo.additional_interests ?? []
                    );
                    setIsEditing(false);
                  }}
                  raised
                  severity="secondary"
                  text
                />
                <Button
                  label={"Save"}
                  loading={updateReport.isPending}
                  disabled={
                    isEqual(originalSafeCompanyInfo, safeCompanyInfo) &&
                    isEqual(
                      additionalInterests,
                      rawCompanyInfo.additional_interests
                    )
                  }
                  onClick={async () => {
                    const allKeys = Array.from(
                      Object.keys(safeCompanyInfo)
                    ) as (keyof client.RawCompanyInfo)[];
                    const newCompanyInfo = cloneDeep(rawCompanyInfo);
                    for (const key of allKeys) {
                      const newValue = safeCompanyInfo[key];

                      if (
                        newValue !== undefined &&
                        newValue !== null &&
                        !isEqual(newValue, originalSafeCompanyInfo[key])
                      ) {
                        newCompanyInfo.private_overrides[key] = newValue;
                      }

                      // Drop fields that are the same as their original values
                      if (
                        isEqual(
                          newValue,
                          newCompanyInfo.private_original_values[key]
                        )
                      ) {
                        delete newCompanyInfo.private_overrides[key];
                      }
                    }
                    newCompanyInfo.additional_interests = additionalInterests;
                    const result = await updateReport.mutateAsync({
                      companyInfo: newCompanyInfo,
                    });
                    if (result.did_write_to_bsp_repo) {
                      toast.current?.show({
                        severity: "info",
                        summary: "Wrote row to BSP repo",
                        detail:
                          "This update resulted in a write to the BSP repo. The client file folder and rater will be automatically linked shortly.",
                        sticky: true,
                      });
                    }
                    setIsEditing(false);
                  }}
                  raised
                  text
                />
              </>
            )}
          </div>
        }
      >
        <div
          className={styles.verticalContainer}
          style={{ marginTop: "-20px" }}
        >
          <span className={styles.header} style={{ marginTop: "0px" }}>
            GENERAL
          </span>
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Named Insured"
            field="named_insured"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="DBA"
            field="dba"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Effective Date"
            field="effective_date"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
            placeholder="mm/dd/yyyy"
            disableEditingTooltip={
              isValidEffectiveDate(originalSafeCompanyInfo.effective_date) &&
              !workflowState.linked
                ? "You can only edit the effective date after the client file folder is linked."
                : undefined
            }
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Expiration Date"
            field="expiration_date"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
            placeholder="mm/dd/yyyy"
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Business Description"
            field="business_description"
            enumValues={BUSINESS_DESCRIPTIONS}
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Entity Type"
            field="entity_type"
            enumValues={[
              "LLC",
              "Corporation",
              "Joint Venture",
              "Parternship",
              "Individual",
            ]}
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Years in Business"
            field="years_in_business"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Brokerage"
            field="broker_name"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Broker Name"
            field="broker_agent_name"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Broker Email"
            field="broker_email"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <div className={styles.line} />
          <span className={styles.header}>INSURED MAILING ADDRESS</span>
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Mailing Street Address"
            field="company_address"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Mailing City"
            field="company_city"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Mailing State"
            field="company_state"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label="Mailing Zip Code"
            field="company_zip"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <div className={styles.line} />
          <span className={styles.header}>ADDITIONAL NAMED INSUREDS</span>
          <EditableFlaggedField
            rawCompanyInfo={rawCompanyInfo}
            safeCompanyInfo={safeCompanyInfo}
            label=""
            field="additional_named_insureds"
            isEditing={isEditing}
            setSafeCompanyInfo={setSafeCompanyInfo}
          />
          <div className={styles.line} />
          <span className={styles.header}>ADDITIONAL INTERESTS</span>
          <EditableAdditionalInterests
            additionalInterests={additionalInterests}
            setAdditionalInterests={setAdditionalInterests}
            isEditing={isEditing}
            allAddresses={addresses}
          />
        </div>
        <GoogleSheetsModal
          visible={googleSheetsModalVisible}
          setVisible={setGoogleSheetsModalVisible}
          report={applyOverridesToResponse(specialtyPropertyInfo)}
          sendGeneralInput={true}
        />
      </Card>
    </>
  );
};
